"use client";

import { useEffect, type RefObject } from "react";

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keeps keyboard focus inside a modal surface (drawer, dialog) while it is open, and gives
 * it back to the element that had it before opening once it closes (WCAG 2.4.3, and the
 * WAI-ARIA dialog pattern). Tab on the last control wraps to the first, Shift+Tab on the
 * first wraps to the last.
 *
 * @param containerRef - The modal surface.
 * @param active - Whether the modal is open.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    if (!active) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Refs are attached before effects run, so the surface rendered with `active` is here.
    const root = containerRef.current;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      if (!root) return;
      const items = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (el) => el.getClientRects().length > 0 && !el.closest("[inert]"),
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (e.shiftKey && (current === first || !root.contains(current))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (current === last || !root.contains(current))) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Back to the opener, unless the close itself moved focus somewhere on purpose
      // (a link that navigated, a field the page focused).
      if (opener?.isConnected && (document.activeElement === document.body || root?.contains(document.activeElement))) {
        opener.focus({ preventScroll: true });
      }
    };
  }, [active, containerRef]);
}
